import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import Button from "src/components/Button";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import AuthLayout, {
	AuthLayoutContainer,
} from "src/components/layout/auth-layout/AuthLayout";
import Loader from "src/components/Loader";
import useCampaign from "src/hooks/api/services/auth/useCampaign";
import useAuthSession, {
	SessionValue,
} from "src/hooks/api/services/session/useAuthSession";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import { cn } from "src/lib/utils";

const REFERRAL_TYPE = "CAMPAIGN_SIGNUP";

function CampaignSignupView({ t }: Translation) {
	const { tenant } = useTenant();
	const params = useParams();
	const { campaign, status, actions } = useCampaign({
		id: params.id as string,
	});
	const [selected, setSelected] = useState<string[]>([]);
	const { session, actions: sessionActions } = useAuthSession();

	useEffect(() => {
		const sessionId = sessionActions.getCookie();
		if (sessionId) {
			sessionActions.find(sessionId).catch(() => {
				sessionActions.create({
					type: "COMPANY",
					referral_type: REFERRAL_TYPE,
				});
			});
		} else {
			sessionActions.create({
				type: "COMPANY",
				referral_type: REFERRAL_TYPE,
			});
		}
		actions.get();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSignup = async () => {
		const value = {
			type: "COMPANY",
			referral_type: REFERRAL_TYPE,
			campaign_task_ids: selected,
			campaign_contact_id: params.id as string,
		} as SessionValue;
		if (!session?.id) {
			const newSession = await sessionActions.create(value);
			if (!newSession?.id) {
				return null;
			}
			return sessionActions.redirect(newSession.id);
		}
		await sessionActions.update(session?.id, value);
		await sessionActions.redirect(session.id);
	};

	if (tenant.slug !== "getstagedone") {
		return <Navigate to="/signup" />;
	}

	if (status !== "idle") {
		return (
			<AuthLayout variant="flat">
				<AuthLayoutContainer className="flex justify-center items-center h-full">
					<Loader />
				</AuthLayoutContainer>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout variant="flat">
			{campaign?.id && (
				<AuthLayoutContainer className="md:pt-0 pt-0">
					<div className="flex flex-col max-w-md mx-auto w-full">
						<h1 className="text-2xl mb-1">
							{t("internships.title", {
								name: campaign.company.name,
							})}
						</h1>
						<p className="opacity-70">
							{t("internships.description")}
						</p>
						<div className="flex flex-col max-w-md my-8">
							<p>{t("internships.select.title")}</p>
							<ul className="flex flex-col gap-2 mt-4">
								{campaign.tasks.map((task) => (
									<li
										key={task.id}
										onClick={() =>
											setSelected((selected) =>
												selected.includes(task.id)
													? selected.filter(
															(id) =>
																id !== task.id
													  )
													: [...selected, task.id]
											)
										}
										className={cn(
											"flex flex-col gap-2 p-3 rounded-md bg-card border border-border transition-all duration-300 cursor-pointer hover:border-primary",
											selected.includes(task.id) &&
												"border-primary"
										)}
									>
										<div
											className={cn(
												"w-6 h-6 border border-border rounded text-primary-foreground flex items-center justify-center",
												selected.includes(task.id) &&
													"bg-primary border-primary"
											)}
										>
											<i
												className={cn(
													"fas fa-check text-[12px] opacity-0 transition-all duration-300",
													selected.includes(
														task.id
													) && "opacity-100"
												)}
											></i>
										</div>
										<div className="flex flex-col flex-1">
											<span className="text-[15px]">
												{task.title}
											</span>
											<span className="text-[15px] opacity-70 select-none">
												{task.excerpt}
											</span>
										</div>
									</li>
								))}
							</ul>
						</div>
						<Button className="w-full" onClick={handleSignup}>
							{t(
								selected.length === 0
									? "internships.continue_without"
									: "internships.continue_with",
								{
									count: selected.length,
								}
							)}
						</Button>
					</div>
				</AuthLayoutContainer>
			)}
		</AuthLayout>
	);
}

CampaignSignupView.locale = {
	nl: {
		internships: {
			title: "Hi {{name}}",
			description:
				"Zijn deze stageplekken nog beschikbaar? Publiceer je stageplekken gratis op {{tenant.name}} en vergroot je kans op een topstagiair!",
			select: {
				title: "Selecteer de stageplekken die je wilt publiceren",
			},
			continue_without: "Doorgaan zonder stageplekken",
			continue_with: "Doorgaan met {{count}} stageplekken",
			continue_with_one: "Doorgaan met 1 stageplek",
		},
	},
	en: {},
};
export default withTranslation(CampaignSignupView);
